import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  message,
  Descriptions,
  Tabs,
  Statistic,
  Progress,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  DollarOutlined,
  TrophyOutlined,
  TeamOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import {
  employeeService,
  type CreateEmployeeRequest,
  type GetEmployeesParams,
} from "../services/employeeService";
import type { User } from "../types";
import { useAuthStore } from "../store/authStore";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Password } = Input;

const EmployeesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useState<GetEmployeesParams>({
    page: 1,
    limit: 10,
  });
  const [modal, contextHolder] = Modal.useModal();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<User | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();

  // Fetch employees
  const { data: employeesData, isLoading } = useQuery(
    ["employees", searchParams],
    () => employeeService.getEmployees(searchParams),
    { keepPreviousData: true }
  );

  // Fetch employee stats
  const { data: employeeStats } = useQuery(
    ["employeeStats", selectedEmployee?._id],
    () => employeeService.getEmployeeStats(selectedEmployee!._id),
    { enabled: !!selectedEmployee }
  );

  // Create/Update employee mutation
  const employeeMutation = useMutation(
    (data: CreateEmployeeRequest & { id?: string }) => {
      if (data.id) {
        const { id, password, ...updateData } = data;
        return employeeService.updateEmployee(id, updateData);
      }
      return employeeService.createEmployee(data);
    },
    {
      onSuccess: () => {
        message.success(
          editingEmployee ? "ແກ້ໄຂຂໍ້ມູນພະນັກງານສຳເລັດ" : "ເພີ່ມພະນັກງານສຳເລັດ"
        );
        setIsModalVisible(false);
        setEditingEmployee(null);
        form.resetFields();
        queryClient.invalidateQueries(["employees"]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
      },
    }
  );

  // Delete employee mutation
  const deleteMutation = useMutation(employeeService.deleteEmployee, {
    onSuccess: () => {
      message.success("ລຶບພະນັກງານສຳເລັດ");
      queryClient.invalidateQueries(["employees"]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // Change password mutation
  const passwordMutation = useMutation(
    ({ id, password }: { id: string; password: string }) =>
      employeeService.changePassword(id, password),
    {
      onSuccess: () => {
        message.success("ປ່ຽນລະຫັດຜ່ານສຳເລັດ");
        setPasswordModalVisible(false);
        setSelectedEmployee(null);
        passwordForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
      },
    }
  );

  const handleSearch = (value: string) => {
    setSearchParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleRoleFilter = (role: string) => {
    setSearchParams((prev) => ({ ...prev, role: role || undefined, page: 1 }));
  };

  const handleStatusFilter = (status: string) => {
    setSearchParams((prev) => ({
      ...prev,
      status: status || undefined,
      page: 1,
    }));
  };

  const handleEdit = (employee: User) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      ...employee,
      startDate: employee.startDate ? dayjs(employee.startDate) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (employee: User) => {
    if (employee._id === currentUser?._id) {
      message.warning("ບໍ່ສາມາດລຶບບັນຊີຕົວເອງໄດ້");
      return;
    }
    modal.confirm({
      title: "ຢືນຢັນການລຶບພະນັກງານ",
      content: (
        <div>
          <p>
            ຕ້ອງການລຶບພະນັກງານ "<strong>{employee.name}</strong>" ຫຼື ບໍ່?
          </p>
          <p style={{ color: "#ff4d4f", fontSize: "12px" }}>
            ⚠️ ການດຳເນີນການນີ້ຈະປ່ຽນສະຖານະເປັນ ບໍ່ໃຊ້ງານ ແລະ
            ບໍ່ສາມາດເຂົ້າສູ່ລະບົບໄດ້
          </p>
        </div>
      ),
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລິກ",
      onOk: () => deleteMutation.mutate(employee._id),
    });
  };

  const handleViewDetails = (employee: User) => {
    setSelectedEmployee(employee);
    setDetailModalVisible(true);
  };

  const handleChangePassword = (employee: User) => {
    setSelectedEmployee(employee);
    setPasswordModalVisible(true);
  };

  const handleSubmit = (values: any) => {
    const data = {
      ...values,
      startDate: values.startDate?.format("YYYY-MM-DD"),
      ...(editingEmployee && { id: editingEmployee._id }),
    };
    employeeMutation.mutate(data);
  };

  const handlePasswordSubmit = (values: any) => {
    if (selectedEmployee) {
      passwordMutation.mutate({
        id: selectedEmployee._id,
        password: values.newPassword,
      });
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: "red",
      cashier: "blue",
      therapist: "green",
    };
    return colors[role as keyof typeof colors] || "default";
  };

  const getRoleText = (role: string) => {
    const texts = {
      admin: "ຜູ້ດູແລລະບົບ",
      cashier: "ແຄດເຊຍ",
      therapist: "ໝໍນວດ",
    };
    return texts[role as keyof typeof texts] || role;
  };

  const getStatusColor = (
    status: string
  ): "success" | "error" | "warning" | "processing" | "default" | undefined => {
    const colorMap = {
      active: "success",
      inactive: "error",
      suspended: "warning",
    } as const;

    return colorMap[status as keyof typeof colorMap];
  };

  const getStatusText = (status: string) => {
    const texts = {
      active: "ໃຊ້ງານ",
      inactive: "ບໍ່ໃຊ້ງານ",
      suspended: "ປິດໃຊ້ງານ",
    };
    return texts[status as keyof typeof texts] || status;
  };

  const columns = [
    {
      title: "ພະນັກງານ",
      key: "employee",
      render: (_: any, record: User) => (
        <Space>
          <Avatar
            style={{ backgroundColor: getRoleColor(record.role) }}
            icon={<UserOutlined />}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {record.employeeId}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "ຕິດຕໍ່",
      key: "contact",
      render: (_: any, record: User) => (
        <Space direction="vertical" size={0}>
          <Space size="small">
            <PhoneOutlined style={{ color: "#52c41a" }} />
            <Text>{record.phone}</Text>
          </Space>
          {record.email && (
            <Space size="small">
              <MailOutlined style={{ color: "#1890ff" }} />
              <Text>{record.email}</Text>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: "ຕຳແໜ່ງ",
      dataIndex: "role",
      key: "role",
      render: (role: string) => (
        <Tag color={getRoleColor(role)} icon={<TeamOutlined />}>
          {getRoleText(role)}
        </Tag>
      ),
      filters: [
        { text: "ຜູ້ດູແລລະບົບ", value: "admin" },
        { text: "ແຄດເຊຍ", value: "cashier" },
        { text: "ໝໍນວດ", value: "therapist" },
      ],
    },
    {
      title: "ເງິນເດືອນພື້ນຖານ",
      dataIndex: "baseSalary",
      key: "baseSalary",
      render: (salary: number) => (
        <Space>
          <DollarOutlined style={{ color: "#faad14" }} />
          <Text strong>{salary?.toLocaleString() || 0} ກີບ</Text>
        </Space>
      ),
      sorter: (a: User, b: User) => (a.baseSalary || 0) - (b.baseSalary || 0),
    },
    {
      title: "ຄ່າຄອມມິດຊັ່ນ",
      dataIndex: "commissionRate",
      key: "commissionRate",
      render: (rate: number) => <Text strong>{rate}%</Text>,
      sorter: (a: User, b: User) => a.commissionRate - b.commissionRate,
    },
    {
      title: "ມື້ເລີ່ມວຽກ",
      dataIndex: "startDate",
      key: "startDate",
      render: (date: string) => (
        <Space>
          <CalendarOutlined style={{ color: "#722ed1" }} />
          <Text>{dayjs(date).format("DD/MM/YYYY")}</Text>
        </Space>
      ),
      sorter: (a: User, b: User) =>
        dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Badge status={getStatusColor(status)} text={getStatusText(status)} />
      ),
      filters: [
        { text: "ໃຊ້ງານ", value: "active" },
        { text: "ບໍ່ໃຊ້ງານ", value: "inactive" },
        { text: "ປິດໃຊ້ງານ", value: "suspended" },
      ],
    },
    {
      title: "ຈັດການ",
      key: "actions",
      render: (_: any, record: User) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="ປ່ຽນລະຫັດຜ່ານ">
            <Button
              size="small"
              icon={<LockOutlined />}
              onClick={() => handleChangePassword(record)}
            />
          </Tooltip>
          {record._id !== currentUser?._id && (
            <Tooltip title="ລຶບ">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // const performanceData =
  //   employeeStats?.data && employeeStats?.data?.monthlyPerformance;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          ຈັດການພະນັກງານ
        </Title>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingEmployee(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          ເພີ່ມພະນັກງານໃໝ່
        </Button>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="ພະນັກງານທັງໝົດ"
              value={employeesData?.data.pagination.total || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="ໃຊ້ງານ"
              value={
                employeesData?.data?.employees?.filter(
                  (emp: any) => emp.status === "active"
                ).length || 0
              }
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="ບໍ່ໃຊ້ງານ"
              value={
                employeesData?.data?.employees?.filter(
                  (emp: any) => emp.status === "inactive"
                ).length || 0
              }
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#d9d9d9" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="ປິດໃຊ້ງານ"
              value={
                employeesData?.data?.employees?.filter(
                  (emp: any) => emp.status === "suspended"
                ).length || 0
              }
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input.Search
              placeholder="ຄົ້ນຫາພະນັກງານ..."
              allowClear
              onSearch={handleSearch}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="ເບິ່ງຕາມຕຳແໜ່ງ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleRoleFilter}
            >
              <Option value="admin">ແອັດມິດ</Option>
              <Option value="cashier">ແຄັດເຊຍ</Option>
              <Option value="therapist">ໝໍນວດ</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="ເບິ່ງຕາມສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
            >
              <Option value="active">ໃຊ້ງານ</Option>
              <Option value="inactive">ບໍ່ໃຊ້ງານ</Option>
              <Option value="suspended">ປິດໃຊ້ງານ</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={employeesData?.data?.employees}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: searchParams.page,
            pageSize: searchParams.limit,
            total: employeesData?.data.pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
            onChange: (page, pageSize) =>
              setSearchParams((prev) => ({ ...prev, page, limit: pageSize })),
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {contextHolder}

      {/* Add/Edit Employee Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            {editingEmployee ? "ແກ້ໄຂຂໍ້ມູນພະນັກງານ" : "ເພີ່ມພະນັກງານໃໝ່"}
          </Space>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingEmployee(null);
          form.resetFields();
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            role: "therapist",
            baseSalary: 0,
            workDaysPerMonth: 26,
            workHoursPerDay: 8,
            startWorkTime: "09:00",
            endWorkTime: "18:00",
            breakTimeMinutes: 60,
            commissionRate: 15,
            overtimeRate: 1.5,
            status: "active",
          }}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="ຂໍ້ມູນພື້ນຖານ" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="employeeId"
                    label="ລະຫັດພະນັກງານ"
                    tooltip="ຖ້າບໍ່ລະບຸ ລະບົບຈະສ້າງອັດຕະໂນມັດ"
                  >
                    <Input
                      placeholder="ເຊັ່ນ EMP001 (ຫວ່າງໄວ້ເພື່ອສ້າງອັດຕະໂນມັດ)"
                      disabled={!!editingEmployee}
                      style={{ textTransform: "uppercase" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="ຊື່-ນາມສະກຸນ"
                    rules={[{ required: true, message: "ກະລຸນາປ້ອນຊື່" }]}
                  >
                    <Input placeholder="ຊື່-ນາມສະກຸນ" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="ເບີໂທລະສັບ"
                    rules={[
                      { required: true, message: "ກະລຸນາປ້ອນເບີໂທລະສັບ" },
                      {
                        pattern: /^[0-9+\-\s()]+$/,
                        message: "ຮູບແບບເບີໂທບໍ່ຖືກຕ້ອງ",
                      },
                    ]}
                  >
                    <Input placeholder="020-12345678" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="ອີເມວ"
                    rules={[
                      { type: "email", message: "ຮູບແບບອີເມວບໍ່ຖືກຕ້ອງ" },
                    ]}
                  >
                    <Input placeholder="example@spa.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="role"
                    label="ຕຳແໜ່ງ"
                    rules={[{ required: true, message: "ກະລຸນາເລືອກຕຳແໜ່ງ" }]}
                  >
                    <Select placeholder="ເລືອກຕຳແໜ່ງ">
                      <Option value="admin">
                        <Space>
                          <UserOutlined style={{ color: "red" }} />
                          ແອັດມິນ
                        </Space>
                      </Option>
                      <Option value="cashier">
                        <Space>
                          <DollarOutlined style={{ color: "blue" }} />
                          ແຄສເຊຍ
                        </Space>
                      </Option>
                      <Option value="therapist">
                        <Space>
                          <TrophyOutlined style={{ color: "green" }} />
                          ໝໍນວດ
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="startDate"
                    label="ວັນທີເລີ່ມວຽກ"
                    initialValue={dayjs()}
                  >
                    <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>

              {editingEmployee && (
                <Form.Item name="status" label="ສະຖານະ">
                  <Select>
                    <Option value="active">
                      <Badge status="success" text="ໃຊ້ງານ" />
                    </Option>
                    <Option value="inactive">
                      <Badge status="default" text="ບໍ່ໃຊ້ງານ" />
                    </Option>
                    <Option value="suspended">
                      <Badge status="error" text="ປິດໃຊ້ງານ" />
                    </Option>
                  </Select>
                </Form.Item>
              )}

              {!editingEmployee && (
                <Form.Item
                  name="password"
                  label="ລະຫັດຜ່ານ"
                  rules={[
                    { required: true, message: "ກະລຸນາປ້ອນລະຫັດຜ່ານ" },
                    { min: 6, message: "ລະຫັດຜ່ານຕ້ອງມີ 6 ໂຕຂື້ນໄປ" },
                  ]}
                >
                  <Password placeholder="ລະຫັດຜ່ານ" />
                </Form.Item>
              )}
            </TabPane>

            <TabPane tab="ເງິນເດືອນ & ຄ່າຕອບແທນ" key="salary">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="baseSalary"
                    label="ເງິນເດືອນພື້ນຖານ (ກີບ)"
                    tooltip="ເງິນເດືອນພື້ນຖານປະຈຳເດືອນ"
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="3000000"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value: any) => value!.replace(/\$\s?|(,*)/g, "")}
                      addonAfter="ກີບ"
                    />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
                  <Form.Item
                    name="commissionRate"
                    label="ຄ່າຄອມມິດຊັ່ນ (%)"
                    rules={[
                      { required: true, message: "ກະລຸນາລະບຸຄ່າຄອມມິດຊັ່ນ" },
                    ]}
                    tooltip="ເປີເຊັນຄອມມິດຊັນຈາກການບໍລິການ"
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="15"
                      addonAfter="%"
                    />
                  </Form.Item>
                </Col> */}
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="workDaysPerMonth"
                    label="ວັນເຮັດວຽກຕໍ່ເດືອນ"
                    tooltip="ຈຳນວນວັນເຮັດວຽກມາດຕະຖານຕໍ່ເດືອນ"
                  >
                    <InputNumber
                      min={1}
                      max={31}
                      style={{ width: "100%" }}
                      placeholder="26"
                      addonAfter="ວັນ"
                    />
                  </Form.Item>
                </Col>
                {/* <Col span={12}>
                  <Form.Item
                    name="overtimeRate"
                    label="ອັດຕາໂອທີ"
                    rules={[{ required: true, message: "ກະລຸນາປ້ອນອັດຕາໂອທີ" }]}
                    tooltip="ຕົວຄູນສຳລັບຄິດຄ່າລ່ວງເວລາ"
                  >
                    <InputNumber
                      min={1}
                      max={5}
                      step={0.1}
                      style={{ width: "100%" }}
                      placeholder="1.5"
                      addonAfter="ເທົ່າ"
                    />
                  </Form.Item>
                </Col> */}
              </Row>
            </TabPane>

            <TabPane tab="ເວລາເຮັດວຽກ" key="worktime">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="workHoursPerDay"
                    label="ຊົ່ວໂມງເຮັດວຽກຕໍ່ວັນ"
                    tooltip="ຈຳນວນຊົ່ວໂມງເຮັດວຽກມາດຕະຖານຕໍ່ວັນ"
                  >
                    <InputNumber
                      min={1}
                      max={24}
                      style={{ width: "100%" }}
                      placeholder="8"
                      addonAfter="ຊົ່ວໂມງ"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="breakTimeMinutes"
                    label="ເວລາພັກ (ນາທີ)"
                    tooltip="ເວລາພັກລວມຕໍ່ວັນ"
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="60"
                      addonAfter="ນາທີ"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="startWorkTime" label="ເວລາເລີ່ມວຽກ">
                    <Input
                      placeholder="09:00"
                      addonBefore={<ClockCircleOutlined />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="endWorkTime" label="ເວລາເລີກວຽກ">
                    <Input
                      placeholder="18:00"
                      addonBefore={<ClockCircleOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
          </Tabs>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={employeeMutation.isLoading}
                icon={editingEmployee ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingEmployee ? "ແກ້ໄຂ" : "ເພີ່ມ"}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingEmployee(null);
                  form.resetFields();
                }}
              >
                ຍົກເລິກ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Employee Detail Modal */}
      <Modal
        title={
          <Space>
            <Avatar
              style={{
                backgroundColor: selectedEmployee
                  ? getRoleColor(selectedEmployee.role)
                  : "#1890ff",
              }}
              icon={<UserOutlined />}
            />
            ລາຍລະອຽດພະນັກງານ: {selectedEmployee?.name}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedEmployee(null);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        {selectedEmployee && (
          <Tabs defaultActiveKey="info">
            <TabPane tab="ຂໍ້ມູນທົ່ວໄປ" key="info">
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ບໍລິການທັງໝົດ"
                      value={employeeStats?.data?.totalServices || 0}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ຄ່າຄອມລວມ"
                      value={employeeStats?.data?.totalCommission?.LAK || 0}
                      suffix="₭"
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ຄວາມພຶງພໍໃຈ"
                      value={employeeStats?.data?.customerSatisfaction || 0}
                      suffix="%"
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ລູກຄ້າໃຊ້ບໍລິການ"
                      value={employeeStats?.data?.activeCustomers || 0}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Card>
                </Col>
              </Row>
              <Descriptions bordered column={2}>
                <Descriptions.Item label="ລະຫັດພະນັກງານ">
                  <Text strong>{selectedEmployee.employeeId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ຊື່-ນາມສະກຸນ">
                  <Text strong>{selectedEmployee.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ເບີໂທ">
                  <Space>
                    <PhoneOutlined style={{ color: "#52c41a" }} />
                    {selectedEmployee.phone}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ອີເມວ">
                  {selectedEmployee.email ? (
                    <Space>
                      <MailOutlined style={{ color: "#1890ff" }} />
                      {selectedEmployee.email}
                    </Space>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="ຕຳແໜ່ງ">
                  <Tag
                    color={getRoleColor(selectedEmployee.role)}
                    icon={<TeamOutlined />}
                  >
                    {getRoleText(selectedEmployee.role)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ສະຖານະ">
                  <Badge
                    status={getStatusColor(selectedEmployee.status)}
                    text={getStatusText(selectedEmployee.status)}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="ເງິນເດືອນພື້ນຖານ">
                  <Space>
                    <DollarOutlined style={{ color: "#faad14" }} />
                    <Text strong>
                      {(selectedEmployee.baseSalary || 0).toLocaleString()} ກີບ
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ຄ່າຄອມມິດຊັ່ນ">
                  <Text strong>{selectedEmployee.commissionRate}%</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ວັນເຮັດວຽກຕໍ່ເດືອນ">
                  <Text>{selectedEmployee.workDaysPerMonth || 26} ວັນ</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ຊົ່ວໂມງເຮັດວຽກຕໍ່ວັນ">
                  <Text>{selectedEmployee.workHoursPerDay || 8} ຊົ່ວໂມງ</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ເວລາເຮັດວຽກ">
                  <Space>
                    <ClockCircleOutlined style={{ color: "#722ed1" }} />
                    <Text>
                      {selectedEmployee.startWorkTime || "09:00"} -{" "}
                      {selectedEmployee.endWorkTime || "18:00"}
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ອັດຕາໂອທີ">
                  <Text strong>{selectedEmployee.overtimeRate} ເທົ່າ</Text>
                </Descriptions.Item>
                <Descriptions.Item label="ວັນທີເລີ່ມວຽກ">
                  <Space>
                    <CalendarOutlined style={{ color: "#722ed1" }} />
                    {dayjs(selectedEmployee.startDate).format("DD/MM/YYYY")}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="ອາຍຸງານ">
                  <Text>
                    {dayjs().diff(dayjs(selectedEmployee.startDate), "month")}{" "}
                    ເດືອນ
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="ວັນທີເຂົ້າລະບົບ">
                  {dayjs(selectedEmployee.createdAt).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="ອັບເດດລ່າສຸດ">
                  {dayjs(selectedEmployee.updatedAt).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>

            <TabPane tab="ຕັ້ງຄ່າ" key="settings">
              <Card title="ການຕັ້ງຄ່າຄ່າຕອບແທນ" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>ເງິນເດືອນພື້ນຖານ</Text>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#1890ff",
                        }}
                      >
                        {(selectedEmployee.baseSalary || 0).toLocaleString()}{" "}
                        ກີບ
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>ອັດຕາໂອທີ</Text>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#faad14",
                        }}
                      >
                        {selectedEmployee.overtimeRate} ເທົ່າ
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>ຄ່າຄອມມິດຊັ່ນປະຈຸບັນ</Text>
                      <div style={{ marginTop: 8 }}>
                        <Progress
                          percent={selectedEmployee.commissionRate}
                          format={(percent) => `${percent}%`}
                          strokeColor={{
                            "0%": "#108ee9",
                            "100%": "#87d068",
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>ເວລາເຮັດວຽກ</Text>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: "18px",
                          color: "#722ed1",
                        }}
                      >
                        <ClockCircleOutlined />{" "}
                        {selectedEmployee.startWorkTime || "09:00"} -{" "}
                        {selectedEmployee.endWorkTime || "18:00"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card title="ການຈັດການບັນຊີ">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>ປ່ຽນລະຫັດຜ່ານ</Text>
                      <div style={{ color: "#666", fontSize: "12px" }}>
                        ແກ້ໄຂລະຫັດຜ່ານເຂົ້າສູ່ລະບົບ
                      </div>
                    </div>
                    <Button
                      icon={<LockOutlined />}
                      onClick={() => handleChangePassword(selectedEmployee)}
                    >
                      ປ່ຽນລະຫັດຜ່ານ
                    </Button>
                  </div>

                  <Divider />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>ແກ້ໄຂຂໍ້ມູນ</Text>
                      <div style={{ color: "#666", fontSize: "12px" }}>
                        ແກ້ໄຂຂໍ້ມູນສ່ວນຕົວ ແລະ ຕັ້ງຄ່າຄ່າຕອບແທນ
                      </div>
                    </div>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setDetailModalVisible(false);
                        handleEdit(selectedEmployee);
                      }}
                    >
                      ແກ້ໄຂຂໍ້ມູນ
                    </Button>
                  </div>
                </Space>
              </Card>
            </TabPane>
          </Tabs>
        )}
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={
          <Space>
            <LockOutlined />
            ປ່ຽນລະຫັດຜ່ານ: {selectedEmployee?.name}
          </Space>
        }
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          setSelectedEmployee(null);
          passwordForm.resetFields();
        }}
        footer={null}
        width={400}
        destroyOnClose
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="newPassword"
            label="ລະຫັດຜ່ານໃໝ່"
            rules={[
              { required: true, message: "ກະລຸນາປ້ອນລະຫັດຜ່ານໃໝ່" },
              { min: 6, message: "ລະຫັດຜ່ານຢ່າງນ້ອຍຕ້ອງມີ 6 ໂຕ" },
            ]}
          >
            <Password placeholder="ລະຫັດຜ່ານໃໝ່" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="ຢືນຢັນລະຫັດຜ່ານໃໝ່"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "ກະລຸນາຢືນຢັນລະຫັດຜ່ານໃໝ່" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("ລະຫັດຜ່ານບໍ່ຕົງກັນ"));
                },
              }),
            ]}
          >
            <Password placeholder="ຢືນຢັນລະຫັດຜ່ານໃໝ່" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setPasswordModalVisible(false);
                  setSelectedEmployee(null);
                  passwordForm.resetFields();
                }}
              >
                ຍົກເລິກ
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={passwordMutation.isLoading}
                icon={<LockOutlined />}
              >
                ປ່ຽນລະຫັດຜ່ານ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeesPage;
